import { Box } from '@opendoor/bricks-next';
import type { BoxProps } from '@opendoor/bricks-next';
import Head from 'next/head';

export type PictureSourceSpec = Array<{
  srcSet: string;
  media: string;
  key: string;
  fallbackSrc?: string;
}>;

// Last-resort fallback when no <source> matches and no real fallback URL exists.
const TRANSPARENT_PIXEL_DATA_URI =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export const Picture: React.FC<{
  preload: boolean;
  lazy: boolean;
  borderRadius?: BoxProps['borderRadius'];
  sources: PictureSourceSpec;
  fallbackSrc?: string;
  objectFit?: React.CSSProperties['objectFit'];
  fetchPriority?: 'high' | 'low' | 'auto';
  alt?: string;
}> = ({
  preload,
  lazy,
  borderRadius,
  sources,
  fallbackSrc,
  objectFit = 'cover',
  fetchPriority,
  alt = '',
}) => {
  const imgSrc = fallbackSrc ?? TRANSPARENT_PIXEL_DATA_URI;

  const pictureElement = (
    <picture style={{ display: 'block', height: '100%', width: '100%' }}>
      {sources.map(({ srcSet, media, key }) => {
        return <source srcSet={srcSet} media={media} key={`${key}-source`}></source>;
      })}
      <img
        src={imgSrc}
        style={{ display: 'block', objectFit, width: '100%', height: '100%' }}
        alt={alt}
        loading={lazy ? 'lazy' : undefined}
        // @ts-expect-error: fetchpriority is a valid attribute but missing from React 18 types
        fetchpriority={fetchPriority}
      />
    </picture>
  );

  return (
    <>
      {preload && (
        <Head>
          {sources.map(({ srcSet, media, key, fallbackSrc: sourceFallbackSrc }) => {
            return (
              <link
                rel="preload"
                as="image"
                href={sourceFallbackSrc ?? imgSrc}
                // @ts-expect-error: react incorrectly capitalizes this if you use
                // the "typescript" suggestion of `imgSrcSet`
                imagesrcset={srcSet}
                media={media}
                key={`${key}-preload`}
                fetchpriority={fetchPriority ?? 'high'}
              />
            );
          })}
        </Head>
      )}
      {borderRadius ? (
        <Box overflow="hidden" borderRadius={borderRadius} height="100%" width="100%">
          {pictureElement}
        </Box>
      ) : (
        pictureElement
      )}
    </>
  );
};

export type PresetPictureSet = Array<{
  minWidth: number;
  maxWidth: number | null;
  // List of preset values here: https://github.com/opendoor-labs/open_listings/blob/dd5e7230ae24d2e79181cb89485efa79aadee705/app/helpers/image_cdn.rb#L6
  preset: string;
  presetScale?: string;
}>;
export type PresetPictureSetOptions = {
  photoUrl: string;
  sizes: PresetPictureSet;
  borderRadius?: BoxProps['borderRadius'];
  debugImageSizes: boolean;
  lazy: boolean;
  dpr?: Array<number> | null;
  preload?: boolean;
  objectFit?: React.CSSProperties['objectFit'];
  fetchPriority?: 'high' | 'low' | 'auto';
  quality?: number;
  alt?: string;
};

const withImageParams = (photoUrl: string, paramsRecord: Record<string, string>) => {
  const hashIndex = photoUrl.indexOf('#');
  const urlWithoutHash = hashIndex === -1 ? photoUrl : photoUrl.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : photoUrl.slice(hashIndex);
  const queryIndex = urlWithoutHash.indexOf('?');
  const baseUrl = queryIndex === -1 ? urlWithoutHash : urlWithoutHash.slice(0, queryIndex);
  const currentQuery = queryIndex === -1 ? '' : urlWithoutHash.slice(queryIndex + 1);
  const params = new URLSearchParams(currentQuery);

  Object.entries(paramsRecord).forEach(([key, value]) => {
    params.set(key, value);
  });

  const query = params.toString();
  return `${baseUrl}${query ? `?${query}` : ''}${hash}`;
};

export const generatePresetPictureSetProps = ({
  photoUrl,
  sizes,
  borderRadius,
  lazy,
  dpr = [1, 2, 3],
  preload = false,
  objectFit,
  fetchPriority,
  quality,
  ...props
}: PresetPictureSetOptions) => {
  const sortedSizes = [...sizes].sort((a, b) => (a.minWidth <= b.minWidth ? -1 : 1));
  const sources: PictureSourceSpec = sortedSizes.map(
    ({ minWidth, maxWidth, preset, presetScale }) => {
      const paramsRecord: Record<string, string> = {
        preset,
        ...(presetScale ? { presetScale } : {}),
        service: 'cosmos',
        auto: 'format,compress',
        ...(quality !== undefined ? { q: `${quality}` } : {}),
      };
      const sourceFallbackSrc = withImageParams(photoUrl, {
        ...paramsRecord,
        ...(dpr && dpr.length > 0 ? { dpr: '1' } : {}),
      });
      const srcSet =
        dpr && dpr?.length > 0
          ? dpr
              .map((d) => {
                const finalParams: Record<string, string> = { ...paramsRecord, dpr: `${d}` };
                return `${withImageParams(photoUrl, finalParams)} ${d}x`;
              })
              .join(', ')
          : sourceFallbackSrc;
      const media =
        maxWidth === null
          ? `(min-width: ${minWidth}px)`
          : `(min-width: ${minWidth}px) and (max-width: ${maxWidth - 1}px)`;
      return { srcSet, media, key: `${photoUrl}-${minWidth}`, fallbackSrc: sourceFallbackSrc };
    },
  );
  const smallest = sortedSizes[0];
  const fallbackSrc = smallest
    ? withImageParams(photoUrl, {
        preset: smallest.preset,
        ...(smallest.presetScale ? { presetScale: smallest.presetScale } : {}),
        service: 'cosmos',
        auto: 'format,compress',
        ...(quality !== undefined ? { q: `${quality}` } : {}),
        ...(dpr && dpr.length > 0 ? { dpr: '1' } : {}),
      })
    : undefined;
  return {
    photoUrl,
    preload,
    lazy,
    borderRadius,
    sources,
    fallbackSrc,
    objectFit,
    fetchPriority,
    ...props,
  };
};

export const generatePresetPictureSet = (props: PresetPictureSetOptions) => {
  const {
    photoUrl,
    preload,
    lazy,
    borderRadius,
    sources,
    fallbackSrc,
    objectFit,
    fetchPriority,
    alt,
  } = generatePresetPictureSetProps(props);
  return (
    <Picture
      key={photoUrl}
      preload={preload}
      lazy={lazy}
      borderRadius={borderRadius}
      sources={sources}
      fallbackSrc={fallbackSrc}
      objectFit={objectFit}
      fetchPriority={fetchPriority}
      alt={alt}
    />
  );
};
